import { useAtomValue, useSetAtom } from 'jotai';
import { ComponentProps } from 'react';
import { useParams } from 'react-router-dom';
import { useLocalStorage } from 'usehooks-ts';

import { MeasureConnected, MeasureResponseCompletionCampaign } from '@grow-therapy-team/seedling-components';

import { PageComponentProps, PaginatedDrawer } from '../../components';
import { PendingFormsDrawerHeader } from '../../components/forms/PendingFormsDrawerHeader';
import { GroupedFormData } from '../../components/forms/types';
import { MeasuresActionsWrapper as BaseMeasuresActions } from '../../components/measures/MeasuresActions';
import { MeasuresContent as BaseMeasuresContent } from '../../components/measures/MeasuresContent';
import { MeasuresDrawer } from '../../components/measures/MeasuresDrawer';
import { MeasureSchemaAndAnswers } from '../../components/measures/types';
import { STORED_ACCESSIBILITY_SETTINGS_KEY } from '../../components/settings-drawer/constants';
import { useCountdown } from '../../hooks/useCountdown';
import { clientUserShortIdAtom } from '../../state';
import {
  VisitorState,
  hasCompletedClientMeasuresAtom,
  visitorDrawerStateAtom,
  visitorGroupedFormsAtom,
  visitorMeasuresAtom,
  visitorStateAtom,
} from '../state';
import { ProviderReadyToSeeYouCard } from '../waiting-room/delayed-entry/ProviderReadyToSeeYouCard';
import { deadlineAtom } from '../waiting-room/delayed-entry/state';
import { usePreSessionPromptState } from '../waiting-room/pre-session-prompt/usePreSessionPromptState';
import { ClientPendingFormsDrawerActions } from './ClientPendingFormsDrawerAction';
import { ClientPendingFormsWrapper as PendingForm } from './ClientPendingFormsWrapper';
import { AppointmentStatus, YoureAllSetForYourAppointmentCard } from './YoureAllSetForYourAppointmentCard';
import { useTrackMeasureStarted } from './useTrackMeasureStarted';

function Measure(props: ComponentProps<typeof MeasureConnected>) {
  const onValuesChange = useTrackMeasureStarted();
  return <MeasureConnected measureHeadingAs='h3' {...props} onValuesChangeCallback={onValuesChange} />;
}

function MeasuresContent(props: ComponentProps<typeof BaseMeasuresContent>) {
  return <BaseMeasuresContent measureComponent={Measure} {...props} />;
}

export function ClientMeasuresDrawer({ countdown }: { countdown: string | null }) {
  const visitorForms = useAtomValue(visitorGroupedFormsAtom);

  if (!visitorForms) return undefined;

  return (
    <>
      <PaginatedDrawer<GroupedFormData>
        contentComponent={PendingForm}
        contentOnly
        drawerBodyProps={{ className: 'bg-neutral-200' }}
        headerComponent={PendingFormsDrawerHeader}
        headingAs='h2'
        countdownComponent={ProviderReadyToSeeYouCard}
        pages={visitorForms}
        countdown={countdown}
        actionsComponent={ClientPendingFormsDrawerActions}
        drawerBodyKeyFn={({ page }) => `pending-forms-drawer.${page?.formShortId}`}
      />
    </>
  );
}

const CLOSE_DRAWER_TIMEOUT = 15_000;

function MeasuresActionsWrapper(props: PageComponentProps<MeasureSchemaAndAnswers>) {
  const clientUserShortId = useAtomValue(clientUserShortIdAtom);
  const { providerShortId } = useParams();
  const setDrawerState = useSetAtom(visitorDrawerStateAtom);
  const { clearPreSessionPrompt } = usePreSessionPromptState();
  const setMeasures = useSetAtom(visitorMeasuresAtom);
  const setHasCompletedClientMeasures = useSetAtom(hasCompletedClientMeasuresAtom);
  const [{ disableNotificationTimeouts }] = useLocalStorage(STORED_ACCESSIBILITY_SETTINGS_KEY, {
    disableNotificationTimeouts: false,
  });
  const handleSuccessfulSubmission = () => {
    if (props.isLastPage) {
      setMeasures([]);
      setHasCompletedClientMeasures(true);
      clearPreSessionPrompt();
      if (!disableNotificationTimeouts) {
        const timeoutId = setTimeout(function closeDrawer() {
          setDrawerState(null);
        }, CLOSE_DRAWER_TIMEOUT);
        return () => clearTimeout(timeoutId);
      }
    }
  };

  return (
    <BaseMeasuresActions
      {...props}
      clientUserShortId={clientUserShortId}
      providerShortId={providerShortId}
      completionCampaign={MeasureResponseCompletionCampaign.WAITING_ROOM}
      onSubmitMeasureSuccess={handleSuccessfulSubmission}
    />
  );
}

function PendingMeasuresDrawerWrapper({ countdown }: { countdown: string | null }) {
  const measures = useAtomValue(visitorMeasuresAtom);
  const pages = measures?.map((schema) => ({
    schema: schema.schema,
    hasSubmitted: false,
    answers: {},
    errors: [],
    provider: schema.providers?.[0] ?? undefined,
  }));
  const visitorState = useAtomValue(visitorStateAtom);
  const deadline = useAtomValue(deadlineAtom);

  const status: AppointmentStatus =
    visitorState === VisitorState.MEETING ? 'in-session' : deadline ? 'provider-ready' : 'waiting';

  if (!pages?.length) {
    return <YoureAllSetForYourAppointmentCard status={status} />;
  }

  return (
    <MeasuresDrawer
      pages={pages}
      headingAs='h3'
      contentComponent={MeasuresContent}
      actionsComponent={MeasuresActionsWrapper}
      countdown={countdown}
      countdownComponent={ProviderReadyToSeeYouCard}
    />
  );
}

export function ClientMeasuresDrawerWrapper() {
  const deadline = useAtomValue(deadlineAtom);
  const setDrawerState = useSetAtom(visitorDrawerStateAtom);
  const countdown = useCountdown({
    deadline,
    onFinish: () => setDrawerState(null),
  });

  return <PendingMeasuresDrawerWrapper countdown={countdown} />;
}
